import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

export default function DatabaseCleanup() {
  const { currentUser, hasRole } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Check if user is admin
  const isAdmin = hasRole('admin') || hasRole('owner');

  useEffect(() => {
    if (isAdmin) {
      loadEvents();
    }
  }, [isAdmin]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      console.log('📋 Loading events from database...');
      
      const eventsRef = collection(db, 'events');
      const snapshot = await getDocs(eventsRef);
      
      const eventsList = [];
      snapshot.forEach((doc) => {
        const eventData = { id: doc.id, ...doc.data() };
        eventsList.push(eventData);
      });
      
      setEvents(eventsList);
      setMessage(`Found ${eventsList.length} events in database`);
      console.log('📅 Events loaded:', eventsList);
    } catch (error) {
      console.error('❌ Error loading events:', error);
      setMessage('Error loading events: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?\n\nThis will affect BOTH:\n• the-apn.web.app\n• the-apn.com\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      console.log(`🗑️ Deleting event: ${eventTitle} (ID: ${eventId})`);
      
      await deleteDoc(doc(db, 'events', eventId));
      
      setMessage(`✅ Deleted event: ${eventTitle}`);
      
      // Reload events
      await loadEvents();
    } catch (error) {
      console.error('❌ Error deleting event:', error);
      setMessage('Error deleting event: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeSponsorsFromEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Remove sponsor pricing from "${eventTitle}"?\n\nThis will affect BOTH:\n• the-apn.web.app\n• the-apn.com`)) {
      return;
    }

    try {
      setLoading(true);
      console.log(`🔧 Removing sponsor pricing from: ${eventTitle}`);
      
      const event = events.find(e => e.id === eventId);
      if (event && event.pricing && event.pricing.sponsor !== undefined) {
        const updatedPricing = {
          member: event.pricing.member,
          nonMember: event.pricing.nonMember
          // Intentionally omitting sponsor pricing
        };
        
        await updateDoc(doc(db, 'events', eventId), {
          pricing: updatedPricing
        });
        
        setMessage(`✅ Removed sponsor pricing from: ${eventTitle}`);
        
        // Reload events
        await loadEvents();
      } else {
        setMessage(`ℹ️ Event "${eventTitle}" doesn't have sponsor pricing`);
      }
    } catch (error) {
      console.error('❌ Error updating event:', error);
      setMessage('Error updating event: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteAllTestEvents = async () => {
    const testEvents = events.filter(event => 
      event.title && (
        event.title.toLowerCase().includes('test') ||
        event.title.toLowerCase().includes('testing') ||
        event.title === 'Testing 123'
      )
    );

    if (testEvents.length === 0) {
      setMessage('ℹ️ No test events found');
      return;
    }

    if (!window.confirm(`Delete ${testEvents.length} test events?\n\nEvents to delete:\n${testEvents.map(e => `• ${e.title}`).join('\n')}\n\nThis will affect BOTH:\n• the-apn.web.app\n• the-apn.com\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      
      for (const event of testEvents) {
        console.log(`🗑️ Deleting test event: ${event.title} (ID: ${event.id})`);
        await deleteDoc(doc(db, 'events', event.id));
      }
      
      setMessage(`✅ Deleted ${testEvents.length} test events`);
      
      // Reload events
      await loadEvents();
    } catch (error) {
      console.error('❌ Error deleting test events:', error);
      setMessage('Error deleting test events: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">Please log in to access this page.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Admin Access Required</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Database Cleanup Tool</h1>
          
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ Important Warning</h3>
            <p className="text-yellow-700">
              This database is shared between <strong>the-apn.web.app</strong> and <strong>the-apn.com</strong>.
              Any changes made here will affect BOTH sites immediately.
            </p>
          </div>

          {message && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">{message}</p>
            </div>
          )}

          <div className="mb-6 flex gap-4">
            <button
              onClick={loadEvents}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh Events'}
            </button>
            
            <button
              onClick={deleteAllTestEvents}
              disabled={loading}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
            >
              Delete All Test Events
            </button>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Events in Database ({events.length})</h2>
            
            {events.length === 0 ? (
              <p className="text-gray-500">No events found</p>
            ) : (
              <div className="grid gap-4">
                {events.map((event) => (
                  <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{event.title}</h3>
                        <p className="text-sm text-gray-600">ID: {event.id}</p>
                        <p className="text-sm text-gray-600">Date: {event.date}</p>
                        {event.pricing && (
                          <div className="text-sm text-gray-600">
                            Pricing: 
                            {event.pricing.member !== undefined && ` Member: $${event.pricing.member}`}
                            {event.pricing.nonMember !== undefined && ` | Non-Member: $${event.pricing.nonMember}`}
                            {event.pricing.sponsor !== undefined && ` | Sponsor: $${event.pricing.sponsor} ⚠️`}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        {event.pricing && event.pricing.sponsor !== undefined && (
                          <button
                            onClick={() => removeSponsorsFromEvent(event.id, event.title)}
                            disabled={loading}
                            className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 disabled:opacity-50"
                          >
                            Remove Sponsor Pricing
                          </button>
                        )}
                        
                        <button
                          onClick={() => deleteEvent(event.id, event.title)}
                          disabled={loading}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
